/**
 * NIΛS Widget Component
 *
 * Dream Commerce sidebar widget with real-time SSE streaming
 * Displays symbolic messages with lambda signatures and consent management
 */

import React, { useState, useEffect, useRef } from 'react'
import { Sparkles, Moon, Sun, Settings, Bell, BellOff, X, AlertCircle } from 'lucide-react'
import { NIASAPI, SymbolicMessage } from '../../lib/api-client'

interface NIASWidgetProps {
  userId: string
  position?: 'left' | 'right'
  onClose?: () => void
  compact?: boolean
}

type ConsentLevel = 'NONE' | 'BASIC' | 'ENHANCED' | 'FULL_SYMBOLIC' | 'DREAM_AWARE'
type EmotionalTone = 'CALM' | 'FOCUSED' | 'CREATIVE' | 'STRESSED' | 'OVERWHELMED' | 'DREAMING'

export const NIASWidget: React.FC<NIASWidgetProps> = ({
  userId,
  position = 'right',
  onClose,
  compact = false,
}) => {
  const [messages, setMessages] = useState<SymbolicMessage[]>([])
  const [consentLevel, setConsentLevel] = useState<ConsentLevel>('BASIC')
  const [emotionalState, setEmotionalState] = useState<EmotionalTone>('CALM')
  const [isDreamMode, setIsDreamMode] = useState(false)
  const [notificationsEnabled, setNotificationsEnabled] = useState(true)
  const [showSettings, setShowSettings] = useState(false)
  const [isConnected, setIsConnected] = useState(false)
  const [connectionError, setConnectionError] = useState<string | null>(null)
  const eventSourceRef = useRef<EventSource | null>(null)
  const messagesEndRef = useRef<HTMLDivElement>(null)

  // Initialize SSE stream
  useEffect(() => {
    const connectStream = () => {
      try {
        const eventSource = NIASAPI.createMessageStream(userId)
        eventSourceRef.current = eventSource

        eventSource.onopen = () => {
          setIsConnected(true)
          setConnectionError(null)
        }

        eventSource.onmessage = (event) => {
          const data = JSON.parse(event.data)

          if (data.type === 'symbolic_message' && data.message) {
            const message: SymbolicMessage = {
              id: data.message.id,
              content: data.message.content,
              tags: data.message.tags || [],
              tier: data.message.tier || 'PUBLIC',
              emotional_tone: data.message.emotional_tone || 'CALM',
              intensity: data.message.intensity || 0.5,
              dream_based: data.message.dream_based || false,
              lambda_signature: data.message.lambda_signature,
              image_url: data.message.image_url,
              expires_at: data.message.expires_at,
            }

            setMessages((prev) => [message, ...prev].slice(0, 50)) // Keep last 50 messages

            // Show browser notification if enabled
            if (notificationsEnabled && !isDreamMode && Notification.permission === 'granted') {
              new Notification('LUKHAS NIΛS', {
                body: message.content.slice(0, 100),
                icon: '/lukhas-logo.png',
                tag: message.id,
              })
            }
          }
        }

        eventSource.onerror = (error) => {
          console.error('NIΛS SSE error:', error)
          setIsConnected(false)
          setConnectionError('Connection lost. Reconnecting...')
          eventSource.close()

          // Attempt reconnection after 5 seconds
          setTimeout(() => connectStream(), 5000)
        }
      } catch (error) {
        console.error('Failed to create NIΛS stream:', error)
        setConnectionError('Failed to connect to NIΛS service')
      }
    }

    connectStream()

    return () => {
      eventSourceRef.current?.close()
    }
  }, [userId, notificationsEnabled, isDreamMode])

  // Auto-scroll to latest message
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  // Request notification permission
  useEffect(() => {
    if (notificationsEnabled && Notification.permission === 'default') {
      Notification.requestPermission()
    }
  }, [notificationsEnabled])

  // Update consent level
  const handleConsentChange = async (newLevel: ConsentLevel) => {
    try {
      await NIASAPI.updateConsent(userId, newLevel)
      setConsentLevel(newLevel)
    } catch (error) {
      console.error('Failed to update consent:', error)
    }
  }

  // Update emotional state
  const handleEmotionalStateChange = async (newState: EmotionalTone) => {
    try {
      const emotionalVector: Record<string, number> = {
        CALM: newState === 'CALM' ? 1.0 : 0.0,
        FOCUSED: newState === 'FOCUSED' ? 1.0 : 0.0,
        CREATIVE: newState === 'CREATIVE' ? 1.0 : 0.0,
        STRESSED: newState === 'STRESSED' ? 1.0 : 0.0,
        OVERWHELMED: newState === 'OVERWHELMED' ? 1.0 : 0.0,
        DREAMING: newState === 'DREAMING' ? 1.0 : 0.0,
      }
      await NIASAPI.updateEmotionalState(userId, emotionalVector)
      setEmotionalState(newState)
    } catch (error) {
      console.error('Failed to update emotional state:', error)
    }
  }

  // Toggle dream mode
  const toggleDreamMode = async () => {
    const newDreamMode = !isDreamMode
    setIsDreamMode(newDreamMode)

    if (newDreamMode) {
      // Process queued dream deliveries
      try {
        await NIASAPI.processDreamSession(userId)
      } catch (error) {
        console.error('Failed to process dream session:', error)
      }
    }
  }

  // Get tier color
  const getTierColor = (tier: string) => {
    const colors: Record<string, string> = {
      PUBLIC: 'text-gray-400',
      PERSONAL: 'text-blue-400',
      CREATIVE: 'text-purple-400',
      ENTERPRISE: 'text-yellow-400',
      QUANTUM: 'text-cyan-400',
    }
    return colors[tier] || 'text-gray-400'
  }

  // Get emotional icon
  const getEmotionalIcon = (tone: EmotionalTone) => {
    const icons: Record<EmotionalTone, string> = {
      CALM: '🧘',
      FOCUSED: '🎯',
      CREATIVE: '✨',
      STRESSED: '😰',
      OVERWHELMED: '😵',
      DREAMING: '💤',
    }
    return icons[tone] || '🧘'
  }

  if (compact) {
    return (
      <div className={`fixed ${position === 'left' ? 'left-4' : 'right-4'} bottom-4 z-50`}>
        <button
          onClick={() => setShowSettings(!showSettings)}
          className="p-4 bg-gradient-to-br from-purple-500/20 to-pink-500/20 border border-purple-500/30 rounded-full shadow-lg backdrop-blur-sm hover:scale-110 transition-all"
        >
          <Sparkles className="w-6 h-6 text-purple-400" />
          {messages.length > 0 && (
            <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
              {messages.length}
            </span>
          )}
        </button>
      </div>
    )
  }

  return (
    <div className={`flex flex-col h-full bg-gray-900/50 backdrop-blur-sm border-l border-gray-800`}>
      {/* Header */}
      <div className="p-4 border-b border-gray-800 bg-gradient-to-br from-purple-500/10 to-pink-500/10">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center space-x-3">
            <Sparkles className="w-6 h-6 text-purple-400" />
            <div>
              <h3 className="text-lg font-bold text-white">NIΛS</h3>
              <p className="text-xs text-gray-400">Dream Commerce</p>
            </div>
          </div>

          <div className="flex items-center space-x-2">
            <button
              onClick={() => setNotificationsEnabled(!notificationsEnabled)}
              className="p-2 hover:bg-gray-800 rounded transition-colors"
              title={notificationsEnabled ? 'Disable notifications' : 'Enable notifications'}
            >
              {notificationsEnabled ? (
                <Bell className="w-4 h-4 text-blue-400" />
              ) : (
                <BellOff className="w-4 h-4 text-gray-400" />
              )}
            </button>

            <button
              onClick={() => setShowSettings(!showSettings)}
              className="p-2 hover:bg-gray-800 rounded transition-colors"
              title="Settings"
            >
              <Settings className="w-4 h-4 text-gray-400" />
            </button>

            {onClose && (
              <button
                onClick={onClose}
                className="p-2 hover:bg-gray-800 rounded transition-colors"
                title="Close widget"
              >
                <X className="w-4 h-4 text-gray-400" />
              </button>
            )}
          </div>
        </div>

        {/* Status Bar */}
        <div className="flex items-center justify-between text-xs">
          <div className="flex items-center space-x-2">
            <div
              className={`w-2 h-2 rounded-full ${
                isConnected ? 'bg-green-500' : 'bg-red-500 animate-pulse'
              }`}
            />
            <span className="text-gray-400">
              {isConnected ? 'Connected' : 'Disconnected'}
            </span>
          </div>

          <button
            onClick={toggleDreamMode}
            className={`flex items-center space-x-1 px-2 py-1 rounded transition-all ${
              isDreamMode
                ? 'bg-blue-500/20 text-blue-400'
                : 'bg-gray-800/50 text-gray-400 hover:bg-gray-800'
            }`}
          >
            {isDreamMode ? <Moon className="w-3 h-3" /> : <Sun className="w-3 h-3" />}
            <span>{isDreamMode ? 'Dream' : 'Wake'}</span>
          </button>
        </div>
      </div>

      {/* Settings Panel */}
      {showSettings && (
        <div className="p-4 border-b border-gray-800 bg-gray-950/50 space-y-4">
          {/* Consent Level */}
          <div>
            <label className="block text-xs font-medium text-gray-400 mb-2">
              Consent Level
            </label>
            <select
              value={consentLevel}
              onChange={(e) => handleConsentChange(e.target.value as ConsentLevel)}
              className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded text-sm text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
            >
              <option value="NONE">None - No messages</option>
              <option value="BASIC">Basic - Critical only</option>
              <option value="ENHANCED">Enhanced - Personalized</option>
              <option value="FULL_SYMBOLIC">Full Symbolic - All messages</option>
              <option value="DREAM_AWARE">Dream Aware - Include dream delivery</option>
            </select>
          </div>

          {/* Emotional State */}
          <div>
            <label className="block text-xs font-medium text-gray-400 mb-2">
              Emotional State
            </label>
            <div className="grid grid-cols-3 gap-2">
              {(['CALM', 'FOCUSED', 'CREATIVE', 'STRESSED', 'OVERWHELMED', 'DREAMING'] as EmotionalTone[]).map(
                (tone) => (
                  <button
                    key={tone}
                    onClick={() => handleEmotionalStateChange(tone)}
                    className={`px-2 py-2 rounded text-xs transition-all ${
                      emotionalState === tone
                        ? 'bg-purple-500/20 text-purple-400 border border-purple-500/30'
                        : 'bg-gray-800/50 text-gray-400 hover:bg-gray-800'
                    }`}
                  >
                    <span className="mr-1">{getEmotionalIcon(tone)}</span>
                    {tone}
                  </button>
                )
              )}
            </div>
          </div>
        </div>
      )}

      {/* Connection Error */}
      {connectionError && (
        <div className="p-3 bg-red-500/10 border-b border-red-500/20 flex items-center space-x-2">
          <AlertCircle className="w-4 h-4 text-red-400" />
          <span className="text-xs text-red-400">{connectionError}</span>
        </div>
      )}

      {/* Messages List */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.length === 0 ? (
          <div className="text-center py-8">
            <Sparkles className="w-12 h-12 text-gray-600 mx-auto mb-3" />
            <p className="text-gray-400 text-sm">No symbolic messages yet</p>
            <p className="text-gray-500 text-xs mt-1">
              Messages will appear here when you receive them
            </p>
          </div>
        ) : (
          messages.map((message) => (
            <div
              key={message.id}
              className={`p-3 rounded-lg border transition-all hover:border-purple-500/50 ${
                message.dream_based
                  ? 'bg-blue-500/10 border-blue-500/20'
                  : 'bg-gray-800/50 border-gray-700'
              }`}
            >
              {/* Message Header */}
              <div className="flex items-start justify-between mb-2">
                <div className="flex items-center space-x-2">
                  <span className={`text-xs font-medium ${getTierColor(message.tier)}`}>
                    {message.tier}
                  </span>
                  {message.dream_based && (
                    <span className="text-xs px-2 py-0.5 bg-blue-500/20 text-blue-400 rounded">
                      Dream
                    </span>
                  )}
                </div>
                <span className="text-xs text-gray-500">{getEmotionalIcon(message.emotional_tone)}</span>
              </div>

              {/* Message Content */}
              <p className="text-sm text-gray-300 leading-relaxed mb-3">{message.content}</p>

              {/* Message Image */}
              {message.image_url && (
                <div className="mb-3 rounded overflow-hidden">
                  <img
                    src={message.image_url}
                    alt="Symbolic message"
                    className="w-full h-auto"
                    loading="lazy"
                  />
                </div>
              )}

              {/* Message Footer */}
              <div className="flex items-center justify-between">
                <div className="flex flex-wrap gap-1">
                  {message.tags.slice(0, 3).map((tag, index) => (
                    <span key={index} className="text-xs px-2 py-0.5 bg-gray-900/50 text-gray-400 rounded">
                      #{tag}
                    </span>
                  ))}
                </div>
                <code className="text-xs text-purple-400 font-mono">
                  {message.lambda_signature}
                </code>
              </div>

              {/* Expiration Warning */}
              {message.expires_at && (
                <div className="mt-2 pt-2 border-t border-gray-700/50 flex items-center space-x-1 text-xs text-yellow-400">
                  <AlertCircle className="w-3 h-3" />
                  <span>Expires: {new Date(message.expires_at).toLocaleString()}</span>
                </div>
              )}
            </div>
          ))
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Footer Stats */}
      <div className="p-3 border-t border-gray-800 bg-gray-950/50">
        <div className="grid grid-cols-3 gap-2 text-center">
          <div>
            <p className="text-lg font-bold text-white">{messages.length}</p>
            <p className="text-xs text-gray-400">Messages</p>
          </div>
          <div>
            <p className="text-lg font-bold text-blue-400">
              {messages.filter((m) => m.dream_based).length}
            </p>
            <p className="text-xs text-gray-400">Dream</p>
          </div>
          <div>
            <p className="text-lg font-bold text-purple-400">{consentLevel}</p>
            <p className="text-xs text-gray-400">Consent</p>
          </div>
        </div>
      </div>
    </div>
  )
}
